import React, { useState, useEffect } from 'react';
import { templateSettings } from './templateSettings';
import { generateRequestsPDF } from './generateRequestsPDF';
import { collectTemplatePDFs } from './collectTemplatePDFs';
import { readRequestsFromWorkbook,validateWorkbook,readWorkbookFromFile } from './readRequestsFromExcel';
import writeFilesToArchive from './writeFilesToArchive';
import { groupRequestsByPageSizeAndSiteManagerZone, getUniqueSiteManagerZones } from './groupRequestsByPageSizeAndSiteManagerZone';
import Loader from '../../layouts/loader/index';
import { getQRSession } from './qrcodeAuditlog';
import Header from "../../layouts/header";
import Footer from '../../layouts/footer/footer';
import * as XLSX from 'xlsx';
import { getFormattedTimestamp } from './excelUtils';
// Sheet names
const sheets = ['Spaces', 'Assets'];

// Column mapping
const columnMapping = {
  url: 'QR code inhoud',
  label1: 'First text',
  label2: 'Second Text',
  label3: 'Third Text',
  copies: 'number of stickers2',
  linguisticRegime: 'Linguistic regime',
  pageSize: 'Page Size',
  orientation: 'Orientation  Space/Asset (landscape/portrait)',
  siteManagerZone: 'SMZ Name'
};

function Qrcode() {
  const [loading, setLoading] = useState(true);
  const [loaderText, setLoaderText] = useState("");
  const [errorMessage, setErrorMessage] = useState('');
  const [sucessMessage, setSuccessMessage] = useState('');
  const [stationNames, setStationNames] = useState([]);
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('Choose file');
  const [isExcelValid, setIsValidExcel] = useState(false);
  const [isQRGenerationCompleted, setIsQRGenerationCompleted] = useState(false);
  const [isAuthorised, setIsAuthorised] = useState(false);

  const [summary, setSummary] = useState(null);
  const [skippedRows, setSkippedRows] = useState([]);

  useEffect(() => {
    const checkAuthorization = async () => {
      setLoading(true);
      const startTime = Date.now();

      const isLocalhost = window.location.hostname === "localhost";
      const bearerToken = localStorage.getItem("TOKEN");
      const qrcodeAuthorized = localStorage.getItem("qrcodeAuthorized");

      const isAuthorized = bearerToken || isLocalhost || qrcodeAuthorized === "true";
      setIsAuthorised(!!isAuthorized);

      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, 2000 - elapsedTime);

      setTimeout(() => {
        setLoading(false);
      }, remainingTime);

      if (!isAuthorized) {
        const interval = setInterval(() => {
          const qrAuth = localStorage.getItem("qrcodeAuthorized");
          if (qrAuth === "true") {
            clearInterval(interval);
            setIsAuthorised(true);
          }
        }, 2000);

        return () => clearInterval(interval);
      }
    };

    checkAuthorization();
  }, []);

const handleFileUpload = async (e) => {
  setLoading(true);
  setLoaderText("Validating Excel...");
  setErrorMessage("");
  setSuccessMessage(""); // ✅ Reset success message at the start

  const localFile = e.target.files?.[0];

  if (localFile) {
    if (
      localFile.type !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' &&
      localFile.type !== 'application/vnd.ms-excel'
    ) {
      setErrorMessage('Please upload a valid Excel file.');
      setLoading(false);
      return;
    }

    setFileName(localFile.name);
    setFile(localFile);

    try {
      const workbook = await readWorkbookFromFile(localFile);
      await validateWorkbook(workbook, columnMapping);

      getQRSession(true, false, true, false);
      setIsValidExcel(true);

      // ✅ Success: Only set success message if no error
      setSuccessMessage("File Validated Successfully. Kindly click on Generate & Download button for generating the QR codes.");
    } catch (error) {
      setErrorMessage(error.message || 'An error occurred during validation.');
      setIsValidExcel(false);
      getQRSession(true, false, false, false);
    } finally {
      setLoading(false);
    }
  }
};


// Reads and aggregates all records/skipped rows across the given sheets of a workbook
const aggregateRequestsFromSheets = async (workbook, sheetNames, columnMapping) => {
  const allRequests = [];
  const allSkipped = [];
  let totalProcessed = 0;
  let totalSuccess = 0;

  for (const sheetName of sheetNames) {
    const result = await readRequestsFromWorkbook(workbook, sheetName, columnMapping);

    if (!result?.records?.length) {
      console.warn(`No valid data in sheet "${sheetName}", skipping.`);
      continue;
    }

    // Efficient push without spreading large arrays
    for (const record of result.records) {
      allRequests.push(record);
    }

    // Avoid pushing undefined or null skipped entries
    if (Array.isArray(result.skipped)) {
      for (const skip of result.skipped) {
        allSkipped.push(skip);
      }
    }

    // Safer counting
    totalProcessed += result?.totalCount || 0;
    totalSuccess += result?.successCount || 0;
  }

  return { allRequests, allSkipped, totalProcessed, totalSuccess };
};

const handleGenerateDownload = async () => {
  if (!file) {
    setErrorMessage("Please upload an Excel file.");
    setSuccessMessage('');
    return;
  }

  setLoading(true);
  setLoaderText("Generating QRCodes...");
  getQRSession(true, true, false, false);

  try {
    // Step 1: Read workbook once
    const workbook = await readWorkbookFromFile(file);

    // Step 2: Process each sheet
    const { allRequests, allSkipped, totalProcessed, totalSuccess } = await aggregateRequestsFromSheets(workbook, sheets, columnMapping);

    // Step 3: Sort records (if indices are present)
    if (allRequests.length && "_sheetIndex" in allRequests[0]) {
      allRequests.sort((a, b) => {
        return a._sheetIndex - b._sheetIndex || a._rowIndex - b._rowIndex;
      });
    }

    if (allRequests.length === 0) {
      setErrorMessage("No valid data found in any sheet.");
      setSuccessMessage('');
      return;
    }

    // Step 4: Group & generate PDFs
    const stationFolders = await getUniqueSiteManagerZones(allRequests);
    setStationNames(stationFolders);

    const requestGroups = await groupRequestsByPageSizeAndSiteManagerZone(allRequests);
    const settingsAndTemplates = await collectTemplatePDFs(templateSettings);

    const generatedFiles = [];

    for (const [outputFileName, reqs] of Object.entries(requestGroups)) {
      reqs.sort((a, b) => {
        return a._sheetIndex - b._sheetIndex || a._rowIndex - b._rowIndex;
      });

      const pdfBlob = await generateRequestsPDF(reqs, settingsAndTemplates);
      generatedFiles.push([`${outputFileName}.pdf`, pdfBlob]);
    }

    const filesObject = Object.fromEntries(generatedFiles);
    await writeFilesToArchive(filesObject, stationFolders);

    // Step 5: Final state updates
    setSummary({
      total: totalProcessed,
      success: totalSuccess,
      skipped: totalProcessed - totalSuccess,
    });

    setSkippedRows(allSkipped);
    getQRSession(true, false, false, true);
    setIsQRGenerationCompleted(true);
  } catch (err) {
    console.error("Error during QR generation:", err);
    setErrorMessage("A technical issue has occurred. Please try regenerating the QR code. If the problem persists, contact the FixMyStation support team for assistance.");
    setSuccessMessage('');
  } finally {
    setLoading(false);
    setFile(null);
    setFileName("Choose File");
  }
};





const handleDownloadReport = () => {
  if (!skippedRows || skippedRows.length === 0) return;

  const grouped = skippedRows.reduce((acc, row) => {
    const tab = row.tabName;
    if (!acc[tab]) acc[tab] = [];
    acc[tab].push(row);
    return acc;
  }, {});

  const workbook = XLSX.utils.book_new();

  ['Spaces', 'Assets'].forEach((tabName) => {
    const rows = grouped[tabName] || [];

    if (rows.length > 0) {
      const worksheet = XLSX.utils.json_to_sheet(rows);
      XLSX.utils.book_append_sheet(workbook, worksheet, tabName);
    }
  });

  const timestamp = getFormattedTimestamp();
  const filename = `Skipped_Rows_Report ${timestamp}.xlsx`;

  XLSX.writeFile(workbook, filename);
};



return loading ? (
  <Loader text={loaderText}/>
) : (
  <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
    <Header disableDropDown={true} />

    <main style={{ flex: 1, textAlign: 'center', marginTop: '20px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <img
          src={'/images/qrsampleImage.png'}
          alt="QR Code"
          style={{ width: '75px', height: '75px', margin: '10px 0' }}
        />
        <span style={{ fontSize: '14px', color: 'GrayText' }}>QR Code Generator</span>
      </div>

      {!isAuthorised ? (
        <span style={{ fontSize: '14px', color: 'GrayText', marginTop: 50 }}>
          You are not Authorised to use QRCode Utility.
        </span>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {isQRGenerationCompleted && (
            <span style={{ fontSize: '14px', color: 'green', marginTop: '20px' }}>
              QR Code Generated Successfully, Please Refresh the Page for New Session.
            </span>
          )}

          {!isQRGenerationCompleted && (
            <>
              {/* Upload UI */}
              <div style={{ display: 'flex', alignItems: 'center', height: '40px' }}>
                <div style={{
                  border: '1px solid #ccc',
                  padding: '0 12px',
                  borderRadius: '4px 0 0 4px',
                  color: '#333',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  height: '100%',
                  minWidth: '500px',
                  boxSizing: 'border-box',
                  lineHeight: '1',
                }}>
                  {fileName}
                </div>

                <label htmlFor="customFile" style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0 16px',
                  backgroundColor: '#006AB3',
                  color: '#fff',
                  borderRadius: '0 4px 4px 0',
                  cursor: 'pointer',
                  height: '100%',
                  boxSizing: 'border-box',
                  fontSize: '14px',
                  lineHeight: '1',
                  marginTop: '7px',
                }}>
                  Browse
                </label>

                <input
                  id="customFile"
                  type="file"
                  accept=".xlsx, .xls"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />
              </div>

              <span style={{ fontSize: '14px', color: 'GrayText', marginTop: '10px' }}>
                Upload file should confirm to a specific template. If you don't have the template,
                please submit a ServiceNow Request.
              </span>

              {errorMessage && (
                <div style={{ color: 'red', marginTop: '10px' }}>{errorMessage}</div>
              )}

              {sucessMessage && (
                <div style={{ color: 'green', marginTop: '10px' }}>{sucessMessage}</div>
              )}

              {!errorMessage && <button
                onClick={handleGenerateDownload}
                style={{
                  padding: '10px 20px',
                  backgroundColor: isExcelValid ? '#006AB3' : '#A9A9A9',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: isExcelValid ? 'pointer' : 'not-allowed',
                  marginTop: '20px',
                }}
                disabled={!isExcelValid}
              >
                Generate & Download
              </button>}
            </>
          )}

          {/* Summary block shown in all cases when available */}
          {summary && (
            <div style={{ marginTop: '30px', width: '400px' }}>
              <h4>Processing Summary</h4>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f2f2f2' }}>
                    <th style={{ border: '1px solid #ddd', padding: '8px' }}>Metric</th>
                    <th style={{ border: '1px solid #ddd', padding: '8px' }}>Value</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Total Records Processed</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>{summary.total}</td>
                  </tr>
                  <tr>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Successfully Processed</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>{summary.success}</td>
                  </tr>
                  <tr>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Skipped / Failed</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>{summary.skipped}</td>
                  </tr>
                </tbody>
              </table>

              {skippedRows.length > 0 && (
                <button
                  onClick={handleDownloadReport}
                  style={{
                    marginTop: '20px',
                    padding: '10px 20px',
                    backgroundColor: '#006AB3',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                  }}
                >
                  Download Report
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </main>

    <Footer
      langCode={"en"}
      style={{
        backgroundColor: '#006AB3',
        color: '#fff',
        padding: '16px',
        textAlign: 'left',
      }}
    />
  </div>
);


}

export default Qrcode;
